/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Threshold, ThresholdDisplay, getDvtThresholdColor } from '../UNSAFE_meterUtils';
import { HTMLAttributesSignalExcluded } from '../UNSAFE_attributeUtils';

/**
 * Validates the min, max, value and step passed to the meter are in appropriate range.
 * @throws { RangeError }
 * @param min The minimum value of the meter.
 * @param max The maximum value of the meter.
 * @param value The metric value of the meter.
 * @param step The step value of the meter.
 */
export function validateRange(min: number, max: number, value: number, step: number): void {
  if (min > max) {
    throw new RangeError('The min must be lower or equal to max.');
  }

  if (value < min || value > max) {
    throw new RangeError('The value must be between min and max.');
  }

  if (step > max - min) {
    throw new RangeError('The step value must be less than the difference of max and min');
  }
}

/**
 * Returns the threshold that the current value belongs to. Returns undefined if the current value does not belong to
 * any threshold or if thresholds is undefined.
 * @param value The value in meter.
 * @param thresholds The list of thresholds.
 * @returns The Threshold that current value belongs to.
 */
export function findThreshold(value: number, thresholds?: Threshold[]): Threshold | undefined {
  if (!thresholds) {
    return;
  }
  if (value <= thresholds[0].max) {
    return thresholds[0];
  }
  let minValue = thresholds[0].max;
  for (let i = 1; i < thresholds.length; i++) {
    if (minValue < value && value <= thresholds[i].max) {
      return thresholds[i];
    }
    minValue = thresholds[i].max;
  }
  return;
}

/**
 * Returns the color of track for the meter.
 * @param trackColor The color provided via trackColor prop of the meter.
 * @param thresholdDisplay  Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param currentThreshold Current threshold for the meter value.
 * @returns The color of the track for the meter.
 */
function getTrackColor(
  thresholdDisplay: ThresholdDisplay,
  currentThreshold: Threshold | undefined,
  trackColor?: string
): string | undefined {
  if (thresholdDisplay === 'track' && currentThreshold?.color) {
    return getDvtThresholdColor(currentThreshold?.color);
  }
  return trackColor;
}

/**
 * Returns the color of indicator for the meter.
 * @param indicatorColor The color provided via indicatorColor prop of the meter.
 * @param thresholdDisplay  Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param currentThreshold Current threshold for the meter value.
 * @returns The color of the indicator of the meter.
 */
function getIndicatorColor(
  thresholdDisplay: ThresholdDisplay,
  currentThreshold: Threshold | undefined,
  indicatorColor?: string
): string | undefined {
  if (currentThreshold?.color && thresholdDisplay === 'indicator') {
    return getDvtThresholdColor(currentThreshold.color);
  }
  return indicatorColor;
}

/**
 * Returns the color of track and indicator of the meter.
 * @param value The value of the meter.
 * @param trackColor The color provided via trackColor prop.
 * @param indicatorColor The color provided via indicatorColor prop.
 * @param thresholdDisplay Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param thresholds The thresholds for metric value of the meter.
 * @returns The color of the indicator and track of the meter.
 */
export function getTrackAndIndicatorColor(
  value: number,
  thresholdDisplay: ThresholdDisplay,
  trackColor?: string,
  indicatorColor?: string,
  thresholds?: Threshold[]
): {
  trackColor?: string;
  indicatorColor?: string;
} {
  const currentThreshold = findThreshold(value, thresholds);
  return {
    trackColor: getTrackColor(thresholdDisplay, currentThreshold, trackColor),
    indicatorColor: getIndicatorColor(thresholdDisplay, currentThreshold, indicatorColor)
  };
}

/**
 * Returns the aria properties of the meter bar.
 * @param value The value of the meter bar.
 * @param min The minimum value of the meter bar.
 * @param max The maximum value of the meter bar.
 * @param ariaLabel The aria label of the meter bar.
 * @param ariaLabelledby The ariaLabelledBy of the meter bar.
 * @param thresholds The thresholds values for the meter bar.
 * @param isDisabled The gauge is disabled or not.
 * @param isReadonly the gauge is readonly or not.
 * @param tooltip The tooltip values for the rating gauge.
 * @returns The aria properties of the meter
 */
export function getMeterAriaProps(
  value: number,
  min: number,
  max: number,
  ariaLabel?: string,
  ariaLabelledby?: string,
  thresholds?: Threshold[],
  isDisabled?: boolean,
  isReadonly?: boolean,
  tooltip?: string
): {
  'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
  'aria-valuenow': HTMLAttributesSignalExcluded['aria-valuenow'];
  'aria-valuemin': HTMLAttributesSignalExcluded['aria-valuemin'];
  'aria-valuetext': HTMLAttributesSignalExcluded['aria-valuetext'];
  'aria-valuemax': HTMLAttributesSignalExcluded['aria-valuemax'];
  'aria-labelledby'?: HTMLAttributesSignalExcluded['aria-labelledby'];
  'aria-disabled'?: HTMLAttributesSignalExcluded['aria-disabled'];
  'aria-readonly'?: HTMLAttributesSignalExcluded['aria-readonly'];
  role: HTMLAttributesSignalExcluded['role'];
} {
  const currentThreshold = findThreshold(value, thresholds);
  const ariaValueText = currentThreshold?.accessibleLabel
    ? `${value} ${currentThreshold.accessibleLabel}`
    : `${value}`;
  const ariaLabelText = ariaLabel
    ? ariaLabel
    : tooltip && isReadonly && !isDisabled
    ? tooltip
    : undefined;
  return {
    'aria-label': ariaLabelText,
    'aria-valuenow': value,
    'aria-valuemin': min,
    'aria-valuetext': ariaValueText,
    'aria-valuemax': max,
    'aria-labelledby': ariaLabelledby,
    'aria-disabled': isDisabled ? true : undefined,
    'aria-readonly': isReadonly && !isDisabled ? true : undefined,
    role: 'slider'
  };
}

/**
 * Returns the threshold color that the current value belongs to. Returns component color if the current value does not belong to
 * any threshold or if thresholds is undefined.
 * @param value The value in meter.
 * @param thresholds The list of thresholds.
 * @returns The threshold color that current value belongs to.
 */
export function getThresholdColorFromValue(value: number, color: any, thresholds?: Threshold[]) {
  const currentThreshold = findThreshold(value, thresholds);
  if (currentThreshold?.color) {
    return currentThreshold?.color;
  }
  return color;
}
